import { useState, useEffect } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trash2, LayoutGrid, PencilLine } from "lucide-react";

/**
 * AdminSbtSolutions — manages the programmatic pages matrix.
 * CMS section: `solutions_matrix`
 *  { industries: [{slug,label,description}],
 *    use_cases:  [{slug,label,description,intent,service_key}],
 *    overrides:  { "{industry_slug}::{use_case_slug}": { headline,answer,bullets,takeaway,faqs,meta_title,meta_description,keywords } } }
 *
 * industries × use_cases combos become auto-generated Astro pages at
 * `/solutions/{industry}/{use_case}/`. Overrides let admins hand-craft
 * copy for any specific combo (e.g. "SaaS × Demand Generation") without
 * touching code.
 */

const API = import.meta.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || "";

const EMPTY = { industries: [], use_cases: [], overrides: {} };

export default function AdminSbtSolutions() {
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingCombo, setEditingCombo] = useState(null); // {industry_slug, use_case_slug}

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/sbt-site/sections/solutions_matrix`);
      const j = await r.json();
      setData({ ...EMPTY, ...(j?.data || {}) });
    } catch { toast.error("Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${API}/api/admin/sbt-site/sections/solutions_matrix`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ data }),
      });
      if (!r.ok) throw new Error(await r.text());
      toast.success("Solutions matrix saved — Astro rebuild queued (may take ~10s to publish 64 combos)");
    } catch (e) { toast.error("Save failed: " + e.message); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="py-10 text-center text-slate-500">Loading solutions matrix…</div>;

  const totalCombos = (data.industries || []).length * (data.use_cases || []).length;

  const setInd = (i, patch) => {
    const next = [...(data.industries || [])];
    next[i] = { ...next[i], ...patch };
    setData({ ...data, industries: next });
  };
  const setUC = (i, patch) => {
    const next = [...(data.use_cases || [])];
    next[i] = { ...next[i], ...patch };
    setData({ ...data, use_cases: next });
  };

  return (
    <div className="space-y-6" data-testid="admin-sbt-solutions">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <LayoutGrid className="w-5 h-5 text-cyan-600" /> Programmatic Solutions Matrix
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            {(data.industries || []).length} industries × {(data.use_cases || []).length} use cases = <span className="font-semibold text-slate-800">{totalCombos} answer-style landing pages</span> auto-generated at
            <code className="text-xs mx-1 px-1.5 py-0.5 rounded bg-slate-100 text-cyan-700">/solutions/{"{industry}/{use_case}/"}</code>.
          </p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="solutions-save-btn">
          {saving ? "Saving…" : "Save & Publish"}
        </Button>
      </div>

      <Tabs defaultValue="industries" className="w-full">
        <TabsList>
          <TabsTrigger value="industries" data-testid="sol-tab-industries">Industries ({(data.industries || []).length})</TabsTrigger>
          <TabsTrigger value="use_cases"  data-testid="sol-tab-use-cases">Use Cases ({(data.use_cases || []).length})</TabsTrigger>
          <TabsTrigger value="overrides"  data-testid="sol-tab-overrides">Per-Combo Overrides ({Object.keys(data.overrides || {}).length})</TabsTrigger>
        </TabsList>

        {/* ── INDUSTRIES ── */}
        <TabsContent value="industries" className="mt-4 space-y-3">
          {(data.industries || []).map((ind, i) => (
            <Card key={i} data-testid={`industry-card-${i}`}>
              <CardContent className="pt-5 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Slug <span className="text-slate-400">(URL segment)</span></Label>
                    <Input value={ind.slug || ""} onChange={(e) => setInd(i, { slug: e.target.value })} data-testid={`industry-slug-${i}`} />
                  </div>
                  <div>
                    <Label className="text-xs">Display label</Label>
                    <Input value={ind.label || ""} onChange={(e) => setInd(i, { label: e.target.value })} data-testid={`industry-label-${i}`} />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Description <span className="text-slate-400">(shown on the industry landing page)</span></Label>
                  <Textarea rows={2} value={ind.description || ""} onChange={(e) => setInd(i, { description: e.target.value })} data-testid={`industry-desc-${i}`} />
                </div>
                <button
                  className="text-xs text-red-500 hover:text-red-700 inline-flex items-center gap-1"
                  onClick={() => setData({ ...data, industries: (data.industries || []).filter((_, k) => k !== i) })}
                  data-testid={`industry-delete-${i}`}
                >
                  <Trash2 className="w-3.5 h-3.5" /> Remove {ind.label}
                </button>
              </CardContent>
            </Card>
          ))}
          <Button variant="outline" onClick={() => setData({ ...data, industries: [...(data.industries || []), { slug: "", label: "", description: "" }] })} data-testid="industry-add">
            <Plus className="w-4 h-4 mr-1" /> Add industry
          </Button>
        </TabsContent>

        {/* ── USE CASES ── */}
        <TabsContent value="use_cases" className="mt-4 space-y-3">
          {(data.use_cases || []).map((uc, i) => (
            <Card key={i} data-testid={`usecase-card-${i}`}>
              <CardContent className="pt-5 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Slug</Label>
                    <Input value={uc.slug || ""} onChange={(e) => setUC(i, { slug: e.target.value })} data-testid={`usecase-slug-${i}`} />
                  </div>
                  <div>
                    <Label className="text-xs">Display label</Label>
                    <Input value={uc.label || ""} onChange={(e) => setUC(i, { label: e.target.value })} data-testid={`usecase-label-${i}`} />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Buyer intent <span className="text-slate-400">(one-line hook used in the answer paragraph)</span></Label>
                  <Input value={uc.intent || ""} onChange={(e) => setUC(i, { intent: e.target.value })} data-testid={`usecase-intent-${i}`} />
                </div>
                <div>
                  <Label className="text-xs">Description <span className="text-slate-400">(optional)</span></Label>
                  <Textarea rows={2} value={uc.description || ""} onChange={(e) => setUC(i, { description: e.target.value })} data-testid={`usecase-desc-${i}`} />
                </div>
                <button
                  className="text-xs text-red-500 hover:text-red-700 inline-flex items-center gap-1"
                  onClick={() => setData({ ...data, use_cases: (data.use_cases || []).filter((_, k) => k !== i) })}
                  data-testid={`usecase-delete-${i}`}
                >
                  <Trash2 className="w-3.5 h-3.5" /> Remove {uc.label}
                </button>
              </CardContent>
            </Card>
          ))}
          <Button variant="outline" onClick={() => setData({ ...data, use_cases: [...(data.use_cases || []), { slug: "", label: "", intent: "", description: "" }] })} data-testid="usecase-add">
            <Plus className="w-4 h-4 mr-1" /> Add use case
          </Button>
        </TabsContent>

        {/* ── OVERRIDES ── */}
        <TabsContent value="overrides" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Per-combo overrides</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xs text-slate-500 mb-4">
                Click any cell to override the auto-templated copy for that combination. Leave a combo blank to keep the auto-templated version.
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs border-collapse">
                  <thead>
                    <tr>
                      <th className="text-left p-2 font-semibold text-slate-600 border-b">Industry ↓ / Use case →</th>
                      {(data.use_cases || []).map((u, j) => (
                        <th key={j} className="text-left p-2 font-semibold text-slate-600 border-b">{u.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(data.industries || []).map((i, ir) => (
                      <tr key={ir}>
                        <td className="p-2 border-b font-semibold text-slate-800">{i.label}</td>
                        {(data.use_cases || []).map((u, uc) => {
                          const k = `${i.slug}::${u.slug}`;
                          const has = !!(data.overrides || {})[k] && Object.keys(data.overrides[k] || {}).some((x) => (data.overrides[k][x] || "").length);
                          return (
                            <td key={uc} className="p-1 border-b">
                              <button
                                onClick={() => setEditingCombo({ i, u, k })}
                                className={`w-full text-left px-2 py-1.5 rounded text-xs font-medium transition ${
                                  has ? "bg-cyan-50 text-cyan-800 border border-cyan-300 hover:bg-cyan-100"
                                      : "bg-slate-50 text-slate-500 border border-transparent hover:border-slate-300 hover:bg-white"
                                }`}
                                data-testid={`override-cell-${i.slug}-${u.slug}`}
                              >
                                {has ? <><PencilLine className="w-3 h-3 inline mr-1" /> edit</> : "auto"}
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {editingCombo && (
            <OverrideDialog
              combo={editingCombo}
              value={(data.overrides || {})[editingCombo.k] || {}}
              onClose={() => setEditingCombo(null)}
              onSave={(next) => {
                const nextOverrides = { ...(data.overrides || {}) };
                if (next && Object.keys(next).length > 0) nextOverrides[editingCombo.k] = next;
                else delete nextOverrides[editingCombo.k];
                setData({ ...data, overrides: nextOverrides });
                setEditingCombo(null);
              }}
            />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function OverrideDialog({ combo, value, onClose, onSave }) {
  const [v, setV] = useState({
    headline: value.headline || "",
    answer: value.answer || "",
    bullets: Array.isArray(value.bullets) ? value.bullets : [],
    takeaway: value.takeaway || "",
    faqs: Array.isArray(value.faqs) ? value.faqs : [],
    meta_title: value.meta_title || "",
    meta_description: value.meta_description || "",
    keywords: Array.isArray(value.keywords) ? value.keywords : [],
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b sticky top-0 bg-white z-10 flex items-center justify-between">
          <div>
            <div className="text-xs text-slate-500 uppercase tracking-wider">Override</div>
            <h4 className="text-base font-semibold text-slate-900">{combo.u.label} for {combo.i.label}</h4>
            <div className="text-xs text-slate-400 mt-0.5">/solutions/{combo.i.slug}/{combo.u.slug}/</div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onSave({})} data-testid="override-reset">Reset to auto</Button>
            <Button onClick={() => onSave(v)} data-testid="override-save">Apply override</Button>
          </div>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <Label className="text-xs">Headline (H1)</Label>
            <Input value={v.headline} onChange={(e) => setV({ ...v, headline: e.target.value })} data-testid="override-headline" />
          </div>
          <div>
            <Label className="text-xs">Answer paragraph <span className="text-slate-400">(the direct answer AI engines will quote)</span></Label>
            <Textarea rows={5} value={v.answer} onChange={(e) => setV({ ...v, answer: e.target.value })} data-testid="override-answer" />
          </div>
          <div>
            <Label className="text-xs">Evidence bullets <span className="text-slate-400">(one per line)</span></Label>
            <Textarea rows={5} value={v.bullets.join("\n")} onChange={(e) => setV({ ...v, bullets: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })} data-testid="override-bullets" />
          </div>
          <div>
            <Label className="text-xs">Takeaway</Label>
            <Textarea rows={2} value={v.takeaway} onChange={(e) => setV({ ...v, takeaway: e.target.value })} data-testid="override-takeaway" />
          </div>
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">FAQs on this page</div>
              <Button variant="outline" size="sm" onClick={() => setV({ ...v, faqs: [...(v.faqs || []), { q: "", a: "" }] })} data-testid="override-add-faq">
                <Plus className="w-4 h-4 mr-1" /> Add FAQ
              </Button>
            </div>
            <div className="space-y-2">
              {(v.faqs || []).map((f, i) => (
                <div key={i} className="border rounded-lg p-3 bg-slate-50" data-testid={`override-faq-${i}`}>
                  <div className="flex justify-end mb-1">
                    <button onClick={() => setV({ ...v, faqs: (v.faqs || []).filter((_, k) => k !== i) })} className="text-slate-400 hover:text-red-500">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <Label className="text-xs">Q</Label>
                  <Input value={f.q} onChange={(e) => { const n = [...v.faqs]; n[i] = { ...n[i], q: e.target.value }; setV({ ...v, faqs: n }); }} className="mb-2" />
                  <Label className="text-xs">A</Label>
                  <Textarea rows={2} value={f.a} onChange={(e) => { const n = [...v.faqs]; n[i] = { ...n[i], a: e.target.value }; setV({ ...v, faqs: n }); }} />
                </div>
              ))}
            </div>
          </div>
          <div className="border-t pt-4 grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Meta title override</Label>
              <Input value={v.meta_title} onChange={(e) => setV({ ...v, meta_title: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Meta keywords (comma-sep)</Label>
              <Input value={v.keywords.join(", ")} onChange={(e) => setV({ ...v, keywords: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Meta description override</Label>
              <Textarea rows={2} value={v.meta_description} onChange={(e) => setV({ ...v, meta_description: e.target.value })} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
